<template>
  <v-container class="pa-2" fluid grid-list-sm>
    <v-card class="shadow-none" v-if="loaded">
      <v-card-text>
        <xReadContent
          :id="20000"
          :format="'array'"
          :my_elementA="dcontent.data.content"
          :runnable="true"
          :task_id="dcontent.task_id"
          :user_course_id="dcontent.user_course_id"
        />
      </v-card-text>

      <!-- EDIT AREA: Prompt (ľavo) + Odpoveď (vpravo ~ 2/3) -->
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12>
            <div class="mb-2">
              <span v-if="!canEdit" class="red--text ml-3">
                Editation locked – teacher comment exists.
              </span>
            </div>
          </v-flex>

          <v-flex xs12 v-for="(pair, idx) in exchanges" :key="'ex-'+idx">
            <v-card class="ex-card mb-4" outlined>
              <v-layout row wrap class="mt-2" style="margin-left: 5px;">
                <!-- ľavý stĺpec: Prompt -->
                <v-flex xs12 sm4>
                  <v-textarea
                    :label="`-> GenAI prompt #${idx+1}`"
                    v-model="pair.prompt"
                    :readonly="!canEdit"
                    outlined
                    filled
                    flat
                    :rows="4"
                    @blur="saveCode"
                  />
                  <v-textarea
                    :label="`-> Reflection #${idx+1}`"
                    v-model="pair.reflection"
                    :readonly="!canEdit"
                    outlined
                    filled
                    flat
                    :rows="4"
                    @blur="saveCode"
                  />
                </v-flex>

                <!-- pravý stĺpec: Odpoveď -->
                <v-flex xs12 sm8>
                  <!-- VIEW režim -->
                  <div
                    v-if="activeReplyIndex !== idx"
                    class="ql-view pa-3"
                    :class="{'is-empty': !(pair.reply && pair.reply.trim())}"
                    role="button"
                    tabindex="0"
                    aria-label="Open reply editor"
                    @click="prepareReplyEdit(idx)"
                    @keyup.enter="prepareReplyEdit(idx)"
                    style="margin-right: 5px;"
                  >
                    <div v-if="pair.reply && pair.reply.trim()" v-html="pair.reply"></div>

                    <div v-else class="reply-empty">
                      <v-icon left small class="mr-2">mdi-text-box-plus-outline</v-icon>
                      <span>GenAI Generated content #{{idx+1}} -—> click to add reply</span>
                    </div>
                  </div>

                  <!-- EDIT režim -->
                  <div v-else v-click-outside="onEditorOutside" style="margin-right: 5px;">
                    <quill-editor
                      :key="'ql-'+idx"
                      v-model="exchanges[idx].reply"
                      :options="getEditorOption()"
                      @blur="onQuillBlur"
                      class="ql-editor-wrap"
                    />
                  </div>
                </v-flex>
              </v-layout>
            </v-card>
          </v-flex>
        </v-layout>

        <v-layout row wrap justify-center align-center>
          <v-flex xs12 class="text-center">
            <div class="mb-2">
              <v-btn
                icon
                aria-label="Add"
                class="mx-2"
                @click="addItem"
                :disabled="!canEdit || exchanges.length >= maxItems"
              >
                <v-icon>mdi-plus-circle-outline</v-icon>
              </v-btn>

              <v-btn
                icon
                aria-label="Delete last"
                class="mx-2"
                @click="removeLastItem"
                :disabled="!canEdit || exchanges.length === 1"
              >
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </div>
          </v-flex>
        </v-layout>
      </v-card-text>

      <!-- HISTÓRIA POKUSOV + KOMENTÁR UČITEĽA -->
      <v-card-text>
        <v-layout row wrap>
          <v-flex xs12 sm3>
            <v-btn
              v-for="(answer, index) in essayAnswers"
              :key="index"
              class="background-transparent"
              style="width:100%"
              @click="showAttempt(answer)"
            >
              {{ answer.time_stamp }} - {{ answer.score }} / {{ answer.max_score }}
              <v-spacer></v-spacer>
              <v-icon v-if="answer.info && answer.info.length>0">done</v-icon>
            </v-btn>
          </v-flex>

          <v-flex xs12 sm9>
            <!-- Prepínanie krokov (karty) v rámci zvoleného attemptu -->
            <div v-if="attemptSteps.length > 0">
              <v-layout row wrap align-center class="mb-2">
                <v-btn small outlined class="mr-2" :disabled="attemptStepIndex<=0" @click="prevAttemptStep">
                  <v-icon left small>mdi-chevron-left</v-icon> Prev
                </v-btn>

                <v-btn small outlined class="mr-3" :disabled="attemptStepIndex>=attemptSteps.length-1" @click="nextAttemptStep">
                  Next <v-icon right small>mdi-chevron-right</v-icon>
                </v-btn>

                <div class="caption grey--text">
                  Step {{ attemptStepIndex + 1 }} / {{ attemptSteps.length }}
                </div>

                <v-spacer></v-spacer>

                <!-- rýchle prepnutie na konkrétny krok -->
                <v-btn-toggle v-model="attemptStepIndex" mandatory dense>
                  <v-btn
                    v-for="(s, i) in attemptSteps"
                    :key="'st-'+i"
                    small
                    outlined
                    :value="i"
                  >
                    {{ i + 1 }}
                  </v-btn>
                </v-btn-toggle>
              </v-layout>

              <v-window v-model="attemptStepIndex" class="mb-2">
                <v-window-item v-for="(step, i) in attemptSteps" :key="'win-'+i" :value="i">
                  <v-card outlined>
                    <v-card-title>
                      #{{ i + 1 }}
                    </v-card-title>

                    <v-divider />

                    <v-card-text>
                      <div class="subtitle-2 mb-1">Prompt</div>
                      <div class="prelike">{{ step.prompt }}</div>

                      <v-divider class="my-3" />

                      <div class="subtitle-2 mb-1">GenAI reply</div>
                      <!-- reply je HTML (RTF-ish) -->
                      <div class="ql-output" v-html="step.replyHtml"></div>

                      <v-divider class="my-3" />

                      <div class="subtitle-2 mb-1">Reflection</div>
                      <div class="prelike">{{ step.reflection }}</div>
                    </v-card-text>
                  </v-card>
                </v-window-item>
              </v-window>
            </div>

            <!-- Comment od učiteľa ostáva (mimo textarea) -->
            <v-card v-if="recentComment.length>0" outlined class="mt-2">
              <v-card-title class="subtitle-1">Comment</v-card-title>
              <v-card-text class="font-weight-bold">{{ recentComment }}</v-card-text>
            </v-card>
          </v-flex>


        </v-layout>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import * as logs from '../../assets/log_process.js'
import xReadContent from '../../components/jprcomponents/etype_xread_content.vue'
import { mapGetters } from 'vuex'
import axios from '../../axios-auth.js'
import { quillEditor } from 'vue-quill-editor'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import * as es from "../../assets/editor_settings.js";

export default {
  components: { xReadContent, quillEditor },
  props: {
    dcontent: Object,
    taskIndex: Number
  },
  data () {
    return {
      startTime: null,
      activity_type: 'GenAI',
      task_id: 0,

      // nové: pole dvojíc prompt/odpoveď/reflection, min 1, max 10
      exchanges: [{ prompt: '', reply: '', reflection: '' }],
      maxItems: 10,

      // pre porovnanie pri ukladaní
      lastSavedJson: null,

      // hodnotenia/komentáre
      essayAnswers: [],
      attemptSteps: [],          // NEW: kroky zvoleného attemptu (prompt + replyHtml + reflection)
      attemptStepIndex: 0,       // NEW: aktuálny krok v attempt-e
      recentComment: '',

      loaded: false,
      activeReplyIndex: -1,
    }
  },
  computed: {
    ...mapGetters(['getSaveCode','getCheckAnswerEvent']),

    // ak existuje aspoň jeden pokus s komentárom učiteľa, zablokuj editáciu
    canEdit () {
      return true //!this.essayAnswers.some(a => a && a.info && a.info.length > 0)
    }
  },

  // ✅ NEW: reaguj na klik na "Save" (Vuex event), bez side-effectov v template renderi
  watch: {
    getSaveCode (val) {
      if (val) this.saveCode()
    },
    getCheckAnswerEvent (val) {
      if (val) this.checkAnswer()
    }
  },

  mounted () {
    this.startTime = new Date()
    logs.write_to_log('open', 'course', this.dcontent.course_id, 'task', this.dcontent.task_id, 0, this.$store.getters.getAxiosConfig)
    if (!this.dcontent.passed) {
      this.$store.commit('setTransferAnswer', { index: this.taskIndex, rating: 0 })
    }
    this.initData()
  },
  beforeDestroy () {
    this.saveReplyContent(true)
    logs.write_to_log('close', 'course', this.dcontent.course_id, 'task', this.dcontent.task_id, Math.floor((new Date() - this.startTime) / 1000 + 0.5), this.$store.getters.getAxiosConfig)
  },
  methods: {
    // UI helpers
    addItem () {
      if (!this.canEdit) return
      if (this.exchanges.length < this.maxItems) {
        this.exchanges.push({ prompt: '', reply: '' , reflection: '' })
      }
    },
    removeLastItem () {
      if (!this.canEdit) return
      if (this.exchanges.length > 1) {
        this.exchanges.pop()
        this.saveCode()
      }
    },

    // serializácia obsahu pre uloženie (✅ rovnaký formát ako v "essay" komponente: {_name,_content})
    toPayloadJson () {
      const trimmed = [...this.exchanges]
      return JSON.stringify({
        _name: this.activity_type,
        _content: trimmed
      })
    },

    // ✅ save pri blur aj pri kliknutí na Save (Vuex)
    saveCode () {
      this.$store.commit('setSaveCode', false)
      if (!this.canEdit) return // zamknuté kvôli komentáru učiteľa

      const filesJson = this.toPayloadJson()
      const changed = filesJson !== this.lastSavedJson

      // nekonaj, keď nič nie je vyplnené a zároveň žiadna zmena
      const notEmpty = this.exchanges.some(p =>
        (p.prompt && p.prompt.trim()) ||
        (p.reply && p.reply.trim()) ||
        (p.reflection && p.reflection.trim())
      )
      if (!notEmpty || !changed) return

      const data = {
        task_id: this.task_id,
        activity_type: this.activity_type,
        files: filesJson
      }

      axios.post('/save-program3', data, this.$store.getters.getAxiosConfig)
        .then(() => {
          this.lastSavedJson = filesJson
          // ✅ hláška ako v predošlom komponente
          // this.$store.commit('setFeedback', 'This is info: Saved')
          // ✅ refresh zoznamu odovzdaní (zobraz všetky pokusy)
          this.getMyAnswers()
        })
        .catch(error => {
          this.$store.commit('setFeedback', error)
        })
    },

    initData () {
      this.task_id = this.dcontent.task_id
      const _data = {
        activity_type: this.activity_type
      }

      axios.post('/load-program3/' + this.task_id, _data, this.$store.getters.getAxiosConfig)
        .then(res => {
          const data = (res.data.code == null) ? '' : res.data.code
          try {
            if (data && data.length > 2) {
              const d = JSON.parse(data)
              // console.log(d)

              if (Array.isArray(d._content)) {
                this.exchanges = d._content.map(it => ({
                  prompt: it.prompt || '',
                  reply: it.reply || '',
                  reflection: it.reflection || ''
                }))
                if (this.exchanges.length === 0) this.exchanges = [{ prompt: '', reply: '', reflection: '' }]
              } else {
                // fallback (ak by prišlo niečo iné)
                this.exchanges = [{ prompt: '', reply: '', reflection: '' }]
              }
            } else {
              this.exchanges = [{ prompt: '', reply: '', reflection: '' }]
            }
          } catch (e) {
            this.exchanges = [{ prompt: '', reply: '', reflection: '' }]
          }

          // ✅ baseline po načítaní, aby "changed" fungovalo správne
          this.lastSavedJson = this.toPayloadJson()

          this.loaded = true
        })
        .catch(error => {
          this.$store.commit('setFeedback', error)
        })

      this.getMyAnswers()
    },

    // hodnotenie / odoslanie na backend
    checkAnswer () {
      this.$store.commit('setCheckAnswerEvent', false)
      if (!this.canEdit) return

      // rovnaký princíp ako v essay: list stringov
      const answerList = [ this.toPayloadJson() ]  // toPayloadJson() je string JSON

      const data = {
        task_id: this.dcontent.task_id,
        time_length: Math.floor((new Date() - this.startTime) / 1000 + 0.5),
        task_type_id: this.dcontent.task_type_id,
        answer_list: JSON.stringify(answerList),
        activity_type: this.activity_type
      }

      
      axios.post('/task-evaluate2', data, this.$store.getters.getAxiosConfig)
        .then((res) => {
          this.useEvaluation(res.data)
        })
        .catch(error => {
          this.$store.commit('setFeedback', error)
        })
    },

    useEvaluation (myData) {
      this.$store.commit('setWaitingForResult', false)
      this.$store.commit('setTransferAnswer', { index: this.taskIndex, rating: myData.result.rating })
      this.$store.commit('setUserPerformance', myData.user)
      this.getMyAnswers()
      this.$store.commit('setFeedback', 'This is info: Saved succesfully')
    },

    // ✅ zoznam odovzdaní
    getMyAnswers () {
      const _data = { task_id: this.task_id }
      axios.post('/get-my-answers', _data, this.$store.getters.getAxiosConfig)
        .then(res => {
          this.essayAnswers = res.data || []
        })
        .catch(error => {
          this.$store.commit('setFeedback', error)
        })
    },

    showAttempt (answer) {
      this.recentComment = answer.info || ''
      this.attemptSteps = []
      this.attemptStepIndex = 0

      // answer.answers často prichádza ako JSON pole stringov: ["..."]
      let raw = ''
      try {
        const arr = JSON.parse(answer.answers)
        if (Array.isArray(arr) && arr.length > 0) raw = arr[0]
      } catch (_) {
        // fallback starý substring
        raw = (answer.answers || '')
        if (raw.length > 2) raw = raw.substring(2, raw.length - 2)
      }

      // raw býva JSON string s {_name,_content:[{prompt,reply,reflection}]}
      try {
        const parsed = JSON.parse(raw)
        if (parsed && Array.isArray(parsed._content)) {
          this.attemptSteps = parsed._content.map(it => ({
            prompt: (it.prompt || '').toString(),
            replyHtml: (it.reply || '').toString(),         // HTML z Quillu
            reflection: (it.reflection || '').toString()
          }))
        } else {
          // fallback: ukáž aspoň niečo
          this.attemptSteps = [{
            prompt: raw,
            replyHtml: '',
            reflection: ''
          }]
        }
      } catch (_) {
        // raw nie je JSON -> fallback
        this.attemptSteps = [{
          prompt: raw,
          replyHtml: '',
          reflection: ''
        }]
      }
    },
    prevAttemptStep () {
      if (this.attemptStepIndex > 0) this.attemptStepIndex -= 1
    },
    nextAttemptStep () {
      if (this.attemptStepIndex < this.attemptSteps.length - 1) this.attemptStepIndex += 1
    },


    getCmOptions(_readOnly, _lines = 1, _line_numbers = true) {
      return es.getCmOptionsLines(
        "text/x-python",
        _readOnly,
        _lines,
        _line_numbers
      );
    },

    prepareReplyEdit(idx) {
      if (!this.canEdit) return
      this.saveReplyContent(true)
      this.activeReplyIndex = idx
    },
    onQuillBlur() {
      this.saveReplyContent(false)
    },
    onEditorOutside() {
      this.saveReplyContent(false)
    },

    saveReplyContent(leavingExisting = false) {
      if (this.activeReplyIndex === -1) return
      this.saveCode()
      this.activeReplyIndex = -1
    },

    getEditorOption() {
      return es.getEditorOption();
    },
  }
}
</script>

<style scoped>
.ql-view {
  cursor: pointer;
  border-radius: 8px;
  transition: background-color .15s ease, box-shadow .15s ease;
}
.ql-view:hover {
  background-color: rgba(0,0,0,0.03);
  box-shadow: 0 0 0 1px rgba(0,0,0,0.06) inset;
}

.ql-view.is-empty {
  padding: 0;
}

.reply-empty {
  min-height: 4.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  border: 1px dashed rgba(0,0,0,0.25);
  border-radius: 8px;
  padding: .75rem 1rem;
  color: rgba(0,0,0,0.6);
  background-color: rgba(0,0,0,0.02);
}
.reply-empty:hover {
  background-color: rgba(0,0,0,0.04);
}

.prelike {
  white-space: pre-wrap;
  word-break: break-word;
}

/* aby v-html z Quillu vyzeral normálne aj mimo editora */
.ql-output :deep(p) { margin: 0 0 .5rem 0; }
.ql-output :deep(ul), .ql-output :deep(ol) { padding-left: 1.2rem; margin: .25rem 0; }

</style>
